import React, { useState, useEffect } from 'react';
import { ApiClient } from '../lib/ApiClient';
import { useNavigation } from '../context/NavigationContext';

interface SharedResultProps {
  apiUrl: string;
  publicId: string;
}

interface PublicShareData {
  public_id: string;
  optimization_score: string;
  grade: string;
  transaction_count: number;
  missed_opportunity: string;
  total_spend: string;
  created_at: string;
}

const SharedResult: React.FC<SharedResultProps> = ({ apiUrl, publicId }) => {
  const [shareData, setShareData] = useState<PublicShareData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const { navigate } = useNavigation();

  const apiClient = new ApiClient(apiUrl);

  useEffect(() => {
    loadSharedResult();
  }, [publicId]);

  const loadSharedResult = async () => {
    try {
      setLoading(true);
      const data = await apiClient.getPublicShare(publicId);
      setShareData(data);
    } catch (err: any) {
      console.error('Failed to load shared result:', err);

      // Redirect to simulation with error message
      navigate('/simulation?error=share_not_found');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: string): string => {
    const num = parseFloat(amount);
    return num.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '40px 20px',
        textAlign: 'center',
        fontFamily: 'Poppins, sans-serif'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #ddd',
          borderTop: '4px solid #0984e3',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '20px auto'
        }} />
        <p>Loading shared result...</p>
      </div>
    );
  }

  if (error || !shareData) {
    return (
      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '40px 20px',
        textAlign: 'center',
        fontFamily: 'Poppins, sans-serif'
      }}>
        <div style={{
          background: 'white',
          padding: '40px',
          borderRadius: '15px',
          boxShadow: '0 5px 20px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ color: '#e17055', marginBottom: '20px' }}>
            {error || 'Result Not Found'}
          </h2>
          <p style={{ color: '#636e72', marginBottom: '30px' }}>
            This shared result may have been removed or the link may be incorrect.
          </p>
          <a href="/simulation" style={{
            background: '#0984e3',
            color: 'white',
            padding: '15px 30px',
            borderRadius: '25px',
            textDecoration: 'none',
            fontSize: '16px',
            fontWeight: '600'
          }}>
            Try Your Own Analysis
          </a>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: '800px',
      margin: '0 auto',
      padding: '40px 20px',
      fontFamily: 'Poppins, sans-serif'
    }}>
      <div style={{
        background: 'white',
        padding: '40px',
        borderRadius: '15px',
        boxShadow: '0 5px 20px rgba(0,0,0,0.1)',
        textAlign: 'center',
        marginBottom: '40px'
      }}>
        <h1 style={{
          fontSize: '32px',
          fontWeight: '700',
          color: '#2d3436',
          marginBottom: '30px'
        }}>
          My Stashr Score
        </h1>
        <p style={{ fontSize: '16px', color: '#636e72' }}>
          Credit card rewards optimization analysis
        </p>
      </div>

      {/* Results Card - Same styling as SimulationV2 */}
      <div style={{
        background: 'white',
        padding: '40px',
        borderRadius: '15px',
        boxShadow: '0 5px 20px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #a29bfe, #74b9ff)',
          color: 'white',
          padding: '40px',
          borderRadius: '20px',
          textAlign: 'center',
          margin: '0 auto',
          maxWidth: '500px',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px'
        }}>
          <h3 style={{ fontSize: '24px' }}>Credit Card Optimization Score</h3>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px'
          }}>
            <div style={{
              fontSize: '56px',
              fontWeight: '700',
              lineHeight: '1.1',
              height: 'auto'
            }}>
              {shareData.optimization_score}<small style={{ fontSize: '28px' }}>/100</small>
            </div>
            <div style={{
              fontSize: '48px',
              fontWeight: '700',
              color: '#ff7675',
              lineHeight: '1.1',
              height: 'auto'
            }}>
              {shareData.grade}
            </div>
          </div>

          <div style={{ fontSize: '18px', opacity: 0.9 }}>
            "I got a {shareData.grade} on my credit card optimization! 😬 Missing out on {formatCurrency(shareData.missed_opportunity)} in rewards."
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '20px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '20px', fontWeight: '600' }}>
                {shareData.transaction_count}
              </div>
              <div style={{ fontSize: '11px', opacity: '0.8' }}>TRANSACTIONS</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '20px', fontWeight: '600' }}>12 MONTHS</div>
              <div style={{ fontSize: '11px', opacity: '0.8' }}>ANALYZED</div>
            </div>
          </div>

          <p style={{ fontSize: '14px', opacity: 0.8 }}>
            Can you beat this score? Try Stashr.ai
          </p>

          <div style={{
            display: 'flex',
            gap: '10px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <a href="/simulation" style={{
              background: '#fdcb6e',
              color: 'white',
              padding: '15px 30px',
              border: 'none',
              borderRadius: '25px',
              fontSize: '16px',
              fontWeight: '600',
              textDecoration: 'none',
              display: 'inline-block'
            }}>
              Try My Own Analysis
            </a>
          </div>
        </div>

        <div style={{ textAlign: 'center', marginTop: '30px' }}>
          <p style={{ fontSize: '14px', color: '#636e72' }}>
            Shared on {formatDate(shareData.created_at)}
          </p>
        </div>
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default SharedResult;
